/** The key-value part of the Web Storage API that the console uses. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Wraps a store whose calls can throw (private windows, blocked site data).
 * A failed write keeps the value in memory only, which the session store already holds.
 */
export function guardedStore(inner: KeyValueStore): KeyValueStore {
  return {
    getItem(key) {
      try {
        return inner.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        inner.setItem(key, value);
      } catch {
        return;
      }
    },
    removeItem(key) {
      try {
        inner.removeItem(key);
      } catch {
        return;
      }
    },
  };
}

/** `window.localStorage` itself throws on access when site data is blocked. */
export function browserStore(): KeyValueStore {
  try {
    return guardedStore(window.localStorage);
  } catch {
    return memoryStore();
  }
}

export function memoryStore(): KeyValueStore {
  const values = new Map<string, string>();
  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => void values.set(key, value),
    removeItem: (key) => void values.delete(key),
  };
}

/** A stored value that is not JSON reads as null, the same as a value the schema rejects. */
export function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
